import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail } from 'lucide-react';

const footerServices = {
  'Trademark Services': [
    { name: 'Register Your Trademark', path: '/services/trademark-registration/register-your-trademark' },
    { name: 'TTAB Petition', path: '/services/trademark-registration/ttab-petition' },
    { name: 'Clearance Search Services', path: '/services/trademark-registration/clearance-search-services' },
    { name: 'Trademark Ownership Transfers', path: '/services/trademark-registration/trademark-ownership-transfers' },
    { name: 'Managed Trademark Renewal', path: '/services/trademark-registration/managed-trademark-renewal' },
    { name: 'Submission of Statements of Use', path: '/services/trademark-registration/submission-of-statements-of-use' },
    { name: 'Support & Response for Trademark Office Actions', path: '/services/trademark-registration/support-and-response-for-trademark-office-actions' },
    { name: 'Active Monitoring for Trademark Infringement', path: '/services/trademark-registration/active-monitoring-for-trademark-infringement' }
  ],
  'Copyright Services': [
    { name: 'Register Your Copyright', path: '/services/copyright-registration/register-your-copyright' },
    { name: 'Copyright Enforcement Assistance', path: '/services/copyright-registration/copyright-enforcement-assistance' },
    { name: 'Copyright Transfers & Licensing Support', path: '/services/copyright-registration/copyright-transfers-and-licensing-support' },
    { name: 'Legal Representation for Copyright', path: '/services/copyright-registration/legal-representation-for-copyright' },
    { name: 'Copyright Portfolio Management', path: '/services/copyright-registration/copyright-portfolio-management' },
    { name: 'DMCA Compliance', path: '/services/copyright-registration/dmca-compliance' }
  ],
  'Patent Services': [
    { name: 'Existing Patent Search', path: '/services/patent-services/existing-patent-search' },
    { name: 'Response to Patent Office Actions', path: '/services/patent-services/response-to-patent-office-actions' },
    { name: 'Patent Application Submission', path: '/services/patent-services/patent-application-submission' },
    { name: 'Legal Representation for Patent Protection', path: '/services/patent-services/legal-representation-for-patent-protection' }
  ],
  'Company Formation': [
    { name: 'LLC Formation', path: '/services/company-formation/llc' },
    { name: 'C Corporation', path: '/services/company-formation/c-corporation' },
    { name: 'S Corporation', path: '/services/company-formation/s-corporation' },
    { name: 'Non-Profit Organization', path: '/services/company-formation/non-profit' }
  ],
  'Legal Services': [
    { name: 'Legal Consultation on Partnerships & LLPs', path: '/services/legal-services/legal-consultation-on-partnerships-and-llps' },
    { name: 'Due Diligence', path: '/services/legal-services/due-diligence' },
    { name: 'Business Immigration Law', path: '/services/legal-services/business-immigration-law' },
    { name: 'IT & Commercial Law', path: '/services/legal-services/it-and-commercial-law' },
    { name: 'Data Protection & Privacy Law', path: '/services/legal-services/data-protection-and-privacy-law' }
  ],
  'Additional Services': [
    { name: 'Amazon Brand Registry (US)', path: '/services/additional-services/amazon-brand-registry-us' },
    { name: 'Business Formation Services', path: '/services/additional-services/business-formation-services' },
    { name: 'Company Dissolution', path: '/services/additional-services/company-dissolution' },
    { name: 'Certificate of Good Standing', path: '/services/additional-services/certificate-of-good-standing' },
    { name: 'Tax Advisory & Filing', path: '/services/additional-services/tax-advisory-and-filing' },
    { name: 'CFO Services', path: '/services/additional-services/cfo-services' }
  ]
};

export const Footer = () => {
  return (
    <footer className="bg-brand-navy border-t border-blue-900/20 text-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Logo and Contact Info */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <img src="/logo-white.png" alt="US Brand Experts" className="h-16 w-auto" />
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Safeguarding Innovation,<br />
              Protecting Identity.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p>1300 S Grand Ave Building A</p>
                  <p>Santa Ana, CA 92705</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 text-gray-400">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <a href="tel:+18776380328" className="hover:text-blue-400 transition-colors">
                  +1 (877) 638-0328
                </a>
              </div>
              
              <div className="flex items-center gap-3 text-gray-400">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <a href="mailto:info@usbrandexperts.com" className="hover:text-blue-400 transition-colors">
                  info@usbrandexperts.com
                </a>
              </div>
            </div>
          </div>

          {/* Service Columns */}
          {Object.entries(footerServices).slice(0, 4).map(([category, services], idx) => (
            <div key={idx}>
              <h3 className="text-white font-heading font-bold text-lg mb-5">
                {category}
              </h3>
              <ul className="space-y-3">
                {services.map((service, serviceIdx) => (
                  <li key={serviceIdx} className="flex items-start gap-2">
                    <span className="text-blue-400 flex-shrink-0 mt-0.5">-</span>
                    <Link
                      to={service.path}
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm leading-relaxed"
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Second Row for Additional Services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mt-16 pt-12 border-t border-blue-900/20">
          <div className="lg:col-span-2"></div>
          {Object.entries(footerServices).slice(4).map(([category, services], idx) => (
            <div key={idx} className="lg:col-span-2">
              <h3 className="text-white font-heading font-bold text-lg mb-5">
                {category}
              </h3>
              <ul className="space-y-3">
                {services.map((service, serviceIdx) => (
                  <li key={serviceIdx} className="flex items-start gap-2">
                    <span className="text-blue-400 flex-shrink-0 mt-0.5">-</span>
                    <Link
                      to={service.path}
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm leading-relaxed"
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-blue-900/20 py-6">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © 2026 US Brand Experts. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-blue-400 transition-colors">
                Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-blue-400 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
